/* eslint-disable no-nested-ternary */
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { sortBy } from 'lodash';
import { useMemo, useState } from 'react';
import DataTable, { Column } from '../../components/DataTable';
import FilterDropdown from '../../components/FilterDropdown';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import currency from '../../utils/currency';
import { getComponentCost } from '../../utils/getPriceByUnit';

export type ReportItem = {
  id: string;
  dishName: string;
  price: number;
  cost: number;
  costPercent: number;
  prevCost: number;
  prevCostPercent: number;
  cookingTime: number;
};

type Row = ReportItem;
type Props = {
  timeRange: [number, number];
};

const ReportByDishes = ({ timeRange }: Props) => {
  const { lang } = useLocale();
  const { cafe } = useAdmin();
  const { dishes, categoriesMap, categories, productsMap, semiProductsMap } = useCafe();
  const [categoryId, setCategoryId] = useState('');

  const categoryIds = useMemo(() => categories.map((s) => s.id), [categories]);

  const filteredDishes = useMemo(() => {
    const filteredByCategory = categoryId ? dishes.filter((d) => d.categoryId === categoryId) : dishes;

    return filteredByCategory;
  }, [categoryId, dishes]);

  const rows = useMemo(
    () =>
      filteredDishes.reduce<Row[]>((acc, dish) => {
        const price = Number(dish.price) || 0;
        const cost = dish.components.reduce((acc, c) => acc + getComponentCost(c, productsMap, semiProductsMap), 0);
        const prevCost = dish.components.reduce(
          (acc, c) => acc + getComponentCost(c, productsMap, semiProductsMap, true),
          0
        );

        const hasVariation = dish.variations?.some((v) => v.components.length);

        if (dish.variations && hasVariation) {
          const dishVariationRows = dish.variations.map((v) => {
            const variationCost =
              cost + v.components.reduce((acc, c) => acc + getComponentCost(c, productsMap, semiProductsMap), 0);

            const prevVariationCost =
              prevCost +
              v.components.reduce((acc, c) => acc + getComponentCost(c, productsMap, semiProductsMap, true), 0);

            return {
              id: dish.id + v.name,
              dishName: `${dish.name} (${v.name})`,
              price,
              cost: variationCost,
              costPercent: Math.round((variationCost / price) * 100),
              prevCost: prevVariationCost,
              prevCostPercent: Math.round((prevVariationCost / price) * 100),
              cookingTime: Number(dish.time),
            };
          });

          return [...acc, ...dishVariationRows];
        }

        const row: Row = {
          id: dish.id,
          dishName: dish.name,
          price,
          cost,
          costPercent: Math.round((cost / price) * 100),
          prevCost,
          prevCostPercent: Math.round((prevCost / price) * 100),
          cookingTime: Number(dish.time),
        };
        return [...acc, row];
      }, []),
    [filteredDishes, productsMap, semiProductsMap]
  );

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        name: 'name',
        title: t.name[lang],
        align: 'left',
        render: (r: Row) => r.dishName,
      },
      {
        name: 'price',
        title: `${t.price[lang]}, ${cafe?.currency}`,
        align: 'right',
        render: (r: Row) => currency(r.price),
        sort: (r: Row[]) => sortBy(r, 'price'),
      },
      {
        name: 'cost',
        title: `${t.cost_price[lang]}, ${cafe?.currency}`,
        align: 'right',
        sort: (r: Row[]) => sortBy(r, 'cost'),
        render: (r: Row) => (
          <div>
            <Typography>{currency(r.cost)}</Typography>
            {r.cost !== r.prevCost && (
              <Typography
                variant="body2"
                sx={{
                  lineHeight: 0.8,
                  color: (t) => (r.cost < r.prevCost ? t.palette.success.dark : t.palette.error.dark),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 0.5,
                }}
              >
                {r.cost < r.prevCost ? <ArrowDownward sx={{ fontSize: 14 }} /> : <ArrowUpward sx={{ fontSize: 14 }} />}
                {`${r.cost < r.prevCost ? '' : '+'}${currency(r.cost - r.prevCost)}`}
              </Typography>
            )}
          </div>
        ),
      },
      {
        name: 'cost_percent',
        title: `${t.cost_price[lang]}, %`,
        align: 'right',
        sort: (r: Row[]) => sortBy(r, 'costPercent'),
        render: (r: Row) => (
          <div>
            <Typography>{r.costPercent}</Typography>
            {r.costPercent !== r.prevCostPercent && (
              <Typography
                variant="body2"
                sx={{
                  lineHeight: 0.8,
                  color: (t) => (r.costPercent < r.prevCostPercent ? t.palette.success.dark : t.palette.error.dark),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 0.5,
                }}
              >
                {r.costPercent < r.prevCostPercent ? (
                  <ArrowDownward sx={{ fontSize: 14 }} />
                ) : (
                  <ArrowUpward sx={{ fontSize: 14 }} />
                )}
                {`${r.costPercent < r.prevCostPercent ? '' : '+'}${r.costPercent - r.prevCostPercent}`}
              </Typography>
            )}
          </div>
        ),
      },
      {
        name: 'time',
        title: `${t.cooking_time[lang]}, ${t.minutes_short[lang]}`,
        align: 'right',
        sort: (r: Row[]) => sortBy(r, 'cookingTime'),
        render: (r: Row) => r.cookingTime || '',
      },
    ],
    [cafe?.currency, lang]
  );

  return (
    <DataTable
      customHeader={
        <FilterDropdown
          label={t.category[lang]}
          value={categoryId}
          setValue={setCategoryId}
          options={categoryIds}
          renderOption={(id) => categoriesMap[id]?.name || id}
        />
      }
      rows={rows}
      searchBy="dishName"
      columns={columns}
      size="small"
      showActionButtonOnEmpty={false}
    />
  );
};

export default ReportByDishes;
